import React, { useCallback, useRef } from 'react';
import uploadIcon from '../assets/icons/upload.svg';
import xmarkIcon from '../assets/icons/xmark.svg';

const StaffRewardImageUpload = ({
  images,
  setImages,
  inputId,
  maxImages = 6,
  extraPreviewItems = null,
  existingCount = 0,
}) => {
  const inputRef = useRef(null);

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const addImageFiles = useCallback(
    files => {
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      if (imageFiles.length === 0) return;
      setImages(prev => {
        // Uploads append to whatever is already saved, so the cap has to count
        // the existing images too.
        const room = Math.max(0, maxImages - existingCount - prev.length);
        return [...prev, ...imageFiles.slice(0, room)];
      });
    },
    [existingCount, maxImages, setImages]
  );

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      addImageFiles(Array.from(e.dataTransfer.files || []));
    },
    [addImageFiles]
  );

  const handleFileSelect = useCallback(
    e => {
      addImageFiles(Array.from(e.target.files || []));
      e.target.value = '';
    },
    [addImageFiles]
  );

  const removeImage = useCallback(
    index => {
      setImages(prev => prev.filter((_, i) => i !== index));
    },
    [setImages]
  );

  const hasPreviews = extraPreviewItems != null || images.length > 0;

  return (
    <div className="form-group">
      <div className="upload-area upload-area--split">
        <div className="upload-title">Upload images</div>
        <div className="upload-subtext">Drag and drop images or click to browse</div>
        <div
          className="upload-center"
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="upload-icon">
            <img src={uploadIcon} alt="" />
          </div>
          <div className="upload-text">Drop your images here</div>
          <div className="upload-browse-text">or click to browse your files</div>
          <div className="upload-content">
            <input
              ref={inputRef}
              type="file"
              id={inputId}
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              className="staff-page-button upload-button"
              onClick={() => inputRef.current?.click()}
            >
              <span className="staff-page-button-text">Choose Images</span>
            </button>
          </div>
        </div>

        {hasPreviews ? (
          <div className="image-preview-container">
            {extraPreviewItems}
            {images.map((image, index) => (
              <div key={`${image.name}-${index}`} className="image-preview-item">
                <div className="preview-image-wrapper">
                  <img
                    src={URL.createObjectURL(image)}
                    alt={image.name}
                    className="preview-image"
                  />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => removeImage(index)}
                  >
                    <img src={xmarkIcon} alt="Remove" />
                  </button>
                </div>
                <div className="preview-name">{image.name}</div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StaffRewardImageUpload;
